import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * Shares a URL via the native share sheet where available (mobile browsers),
 * falling back to a clipboard copy elsewhere, with a MatSnackBar confirmation
 * either way. First use of MatSnackBarModule in the app (exported from
 * SharedMaterialModule but previously unused).
 *
 * Both call sites (competition.component.ts and duel.component.ts) sit behind
 * AuthGuard, but their routes still prerender a shell at build time, so every
 * navigator/window access is guarded behind isPlatformBrowser — there is no
 * `navigator` during prerender.
 */
@Injectable({
  providedIn: 'root',
})
export class ShareService {
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private snackBar: MatSnackBar
  ) {}

  /**
   * @param url the link to share/copy
   * @param title optional share-sheet title (ignored by the clipboard fallback)
   * @param text optional share-sheet body text (ignored by the clipboard fallback)
   */
  async share(url: string, title?: string, text?: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text, url });
        this.snackBar.open('Shared', undefined, { duration: 2500 });
        return;
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          // User cancelled the native share sheet — not an error, nothing to report.
          return;
        }
        // Any other native-share failure falls through to the clipboard fallback below.
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this.snackBar.open('Link copied', undefined, { duration: 2500 });
    } catch (error) {
      this.snackBar.open(
        'Could not share or copy the link. Please copy it manually.',
        undefined,
        { duration: 4000 }
      );
    }
  }
}
